/**
 * 一斉通知ページ
 *
 * Cloudflare Queues による非同期処理に対応
 * 送信中はリアルタイムで進捗を表示
 */

import {
  AlertCircle,
  CheckCircle2,
  Clock,
  History,
  Loader2,
  Mail,
  RefreshCw,
  Send,
  Users,
  XCircle,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useBroadcast } from "@/features/notifications/hooks/use-broadcast";

// ============================================
// ユーザー一覧
// ============================================

function UsersList({
  users,
  total,
}: {
  users: { id: string; email: string; name: string; isPremium: boolean }[];
  total: number;
}) {
  const [showAll, setShowAll] = useState(false);
  const displayUsers = showAll ? users : users.slice(0, 10);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-medium">送信対象ユーザー ({total}人)</h3>
      </div>
      <div className="rounded-lg border divide-y max-h-[300px] overflow-auto">
        {displayUsers.map((user) => (
          <div key={user.id} className="flex items-center justify-between p-3">
            <div>
              <p className="font-medium">{user.name}</p>
              <p className="text-sm text-muted-foreground">{user.email}</p>
            </div>
            {user.isPremium && (
              <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full">
                Premium
              </span>
            )}
          </div>
        ))}
      </div>
      {total > 10 && !showAll && (
        <Button variant="outline" size="sm" onClick={() => setShowAll(true)}>
          すべて表示 ({total}人)
        </Button>
      )}
    </div>
  );
}

// ============================================
// 送信確認ダイアログ
// ============================================

function ConfirmDialog({
  open,
  onOpenChange,
  userCount,
  subject,
  onConfirm,
  isSending,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userCount: number;
  subject: string;
  onConfirm: () => void;
  isSending: boolean;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="h-5 w-5" />
            一斉送信の確認
          </DialogTitle>
          <DialogDescription>
            以下の内容で {userCount} 人のユーザーにメールを送信します。
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <div className="rounded-lg bg-muted p-4">
            <p className="text-sm font-medium">件名:</p>
            <p className="text-sm">{subject}</p>
          </div>
          <div className="mt-4 rounded-lg border border-yellow-200 bg-yellow-50 p-4">
            <p className="text-sm text-yellow-800">
              この操作は取り消せません。送信前に内容を確認してください。
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSending}>
            キャンセル
          </Button>
          <Button onClick={onConfirm} disabled={isSending}>
            {isSending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                送信開始中...
              </>
            ) : (
              <>
                <Send className="mr-2 h-4 w-4" />
                送信する
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================
// 進捗表示カード
// ============================================

type JobStatus = "pending" | "processing" | "completed" | "failed";

interface StatusConfig {
  icon: typeof CheckCircle2;
  color: string;
  text: string;
  animate: boolean;
}

const STATUS_CONFIG: Record<JobStatus, StatusConfig> = {
  completed: { icon: CheckCircle2, color: "text-green-600", text: "送信完了", animate: false },
  failed: { icon: XCircle, color: "text-red-600", text: "送信失敗", animate: false },
  processing: { icon: Loader2, color: "text-primary", text: "送信中...", animate: true },
  pending: { icon: Clock, color: "text-primary", text: "待機中", animate: false },
};

function getStatusConfig(status: string): StatusConfig {
  return STATUS_CONFIG[status as JobStatus] ?? STATUS_CONFIG.pending;
}

interface JobProgressInfo {
  jobId: string;
  status: string;
  totalUsers: number;
  sentCount: number;
  failedCount: number;
  failedEmails: string[];
  subject: string;
  startedAt: string;
  completedAt?: string;
}

function ProgressCard({ job, onClose }: { job: JobProgressInfo; onClose: () => void }) {
  const processedCount = job.sentCount + job.failedCount;
  const progress = job.totalUsers > 0 ? (processedCount / job.totalUsers) * 100 : 0;
  const config = getStatusConfig(job.status);
  const StatusIcon = config.icon;
  const showCloseButton = job.status === "completed" || job.status === "failed";

  return (
    <Card className="border-primary/50 bg-primary/5">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2">
          <StatusIcon
            className={`h-5 w-5 ${config.color} ${config.animate ? "animate-spin" : ""}`}
          />
          {config.text}
        </CardTitle>
        <CardDescription>件名: {job.subject}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <div className="flex items-center justify-between text-sm mb-2">
            <span>進捗</span>
            <span>
              {processedCount} / {job.totalUsers}
            </span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        <div className="grid grid-cols-3 gap-4 text-center">
          <div className="rounded-lg bg-white p-2">
            <p className="text-lg font-bold">{job.totalUsers}</p>
            <p className="text-xs text-muted-foreground">対象</p>
          </div>
          <div className="rounded-lg bg-green-50 p-2">
            <p className="text-lg font-bold text-green-600">{job.sentCount}</p>
            <p className="text-xs text-green-600">成功</p>
          </div>
          <div className="rounded-lg bg-red-50 p-2">
            <p className="text-lg font-bold text-red-600">{job.failedCount}</p>
            <p className="text-xs text-red-600">失敗</p>
          </div>
        </div>

        {job.failedEmails.length > 0 && (
          <div>
            <p className="text-sm font-medium mb-1">失敗したメールアドレス:</p>
            <div className="rounded-lg border bg-white p-2 max-h-[80px] overflow-auto text-xs text-muted-foreground">
              {job.failedEmails.join(", ")}
            </div>
          </div>
        )}

        {showCloseButton && (
          <Button variant="outline" size="sm" className="w-full" onClick={onClose}>
            閉じる
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================
// 履歴一覧
// ============================================

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString("ja-JP", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function HistoryList({
  jobs,
  total,
  isLoading,
  onRefresh,
}: {
  jobs: JobProgressInfo[];
  total: number;
  isLoading: boolean;
  onRefresh: () => void;
}) {
  if (isLoading && jobs.length === 0) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (jobs.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <History className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p>送信履歴はありません</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">過去7日間の履歴 ({total}件)</p>
        <Button variant="ghost" size="sm" onClick={onRefresh} disabled={isLoading}>
          <RefreshCw className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
        </Button>
      </div>
      <div className="space-y-3">
        {jobs.map((job) => {
          const config = getStatusConfig(job.status);
          const StatusIcon = config.icon;
          const successRate =
            job.totalUsers > 0 ? Math.round((job.sentCount / job.totalUsers) * 100) : 0;

          return (
            <div
              key={job.jobId}
              className="rounded-lg border p-4 hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <StatusIcon
                      className={`h-4 w-4 flex-shrink-0 ${config.color} ${config.animate ? "animate-spin" : ""}`}
                    />
                    <span className="font-medium truncate">{job.subject}</span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span>{formatDate(job.startedAt)}</span>
                    <span>
                      {job.sentCount}/{job.totalUsers}件送信
                      {job.failedCount > 0 && (
                        <span className="text-red-500 ml-1">({job.failedCount}件失敗)</span>
                      )}
                    </span>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-lg font-bold">{successRate}%</div>
                  <div className="text-xs text-muted-foreground">成功率</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================
// メインページ
// ============================================

export function BroadcastPage() {
  const {
    users,
    total,
    isLoading,
    error,
    isSending,
    currentJob,
    history,
    historyTotal,
    isLoadingHistory,
    startBroadcast,
    clearCurrentJob,
    refreshHistory,
  } = useBroadcast();
  const [subject, setSubject] = useState("");
  const [htmlContent, setHtmlContent] = useState("");
  const [textContent, setTextContent] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  const canSend = subject.trim() && htmlContent.trim() && textContent.trim() && total > 0;

  const handleSend = async () => {
    const result = await startBroadcast(subject.trim(), htmlContent.trim(), textContent.trim());

    if (result) {
      setConfirmOpen(false);
      toast.success(`${result.totalUsers}人へのメール送信を開始しました`);
      // フォームをクリア
      setSubject("");
      setHtmlContent("");
      setTextContent("");
    } else {
      toast.error("送信の開始に失敗しました");
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error && !currentJob) {
    return (
      <Card>
        <CardHeader className="text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-destructive" />
          <CardTitle className="mt-4">エラー</CardTitle>
          <CardDescription>{error}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Mail className="h-6 w-6" />
            一斉通知
          </h1>
          <p className="text-muted-foreground">全ユーザーへ通知メールを送信します</p>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Users className="h-4 w-4" />
          {total} 人のユーザー
        </div>
      </div>

      {/* 進捗表示（送信中の場合） */}
      {currentJob && <ProgressCard job={currentJob} onClose={clearCurrentJob} />}

      <div className="grid gap-6 lg:grid-cols-[1fr_400px]">
        {/* メール作成 */}
        <Card>
          <CardHeader>
            <CardTitle>メール作成</CardTitle>
            <CardDescription>送信するメールの内容を入力してください</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="subject">件名</Label>
              <Input
                id="subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="メールの件名を入力"
                disabled={isSending}
              />
            </div>

            <Separator />

            <Tabs defaultValue="html">
              <TabsList>
                <TabsTrigger value="html">HTML本文</TabsTrigger>
                <TabsTrigger value="text">テキスト本文</TabsTrigger>
              </TabsList>
              <TabsContent value="html" className="space-y-2">
                <Label htmlFor="htmlContent">HTML本文</Label>
                <Textarea
                  id="htmlContent"
                  value={htmlContent}
                  onChange={(e) => setHtmlContent(e.target.value)}
                  placeholder="HTMLメール本文を入力..."
                  className="min-h-[300px] font-mono text-sm"
                  disabled={isSending}
                />
                <p className="text-xs text-muted-foreground">
                  共通のヘッダー・フッターは自動的に追加されます
                </p>
              </TabsContent>
              <TabsContent value="text" className="space-y-2">
                <Label htmlFor="textContent">テキスト本文</Label>
                <Textarea
                  id="textContent"
                  value={textContent}
                  onChange={(e) => setTextContent(e.target.value)}
                  placeholder="テキストメール本文を入力..."
                  className="min-h-[300px] font-mono text-sm"
                  disabled={isSending}
                />
                <p className="text-xs text-muted-foreground">
                  HTMLメールが表示できない環境向けのテキスト版です
                </p>
              </TabsContent>
            </Tabs>

            <div className="flex justify-end">
              <Button onClick={() => setConfirmOpen(true)} disabled={!canSend || isSending}>
                <Send className="mr-2 h-4 w-4" />
                送信確認
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* サイドバー */}
        <div className="space-y-6">
          {/* ユーザー一覧 */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                送信対象
              </CardTitle>
              <CardDescription>メールが送信されるユーザーの一覧</CardDescription>
            </CardHeader>
            <CardContent>
              <UsersList users={users} total={total} />
            </CardContent>
          </Card>

          {/* 送信履歴 */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="h-5 w-5" />
                送信履歴
              </CardTitle>
              <CardDescription>過去の一斉送信の結果</CardDescription>
            </CardHeader>
            <CardContent>
              <HistoryList
                jobs={history}
                total={historyTotal}
                isLoading={isLoadingHistory}
                onRefresh={refreshHistory}
              />
            </CardContent>
          </Card>
        </div>
      </div>

      {/* 確認ダイアログ */}
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        userCount={total}
        subject={subject}
        onConfirm={handleSend}
        isSending={isSending}
      />
    </div>
  );
}
